import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { FundCurrency } from '@agio/core/domain';
import { fmtMoney } from './utils.js';

/**
 * Which currency each fund's figures are in. An admin sets it on the fund; the
 * portal only formats, it never converts. An amount that spans funds in more
 * than one currency, or belongs to a fund this page does not know, is written
 * in shekels, as everything was before.
 */
const FundCurrencyContext = createContext<ReadonlyMap<string, FundCurrency>>(new Map());

/** One fund's currency, as the server's currencies endpoint lists it — hidden funds included. */
export interface FundCurrencyRow {
  fund_id: string;
  currency: FundCurrency;
}

export function FundCurrencyProvider({
  funds,
  children,
}: {
  funds: FundCurrencyRow[];
  children: ReactNode;
}) {
  const currencies = useMemo(() => new Map(funds.map((f) => [f.fund_id, f.currency])), [funds]);
  return <FundCurrencyContext.Provider value={currencies}>{children}</FundCurrencyContext.Provider>;
}

/** The one currency a set of funds share; shekels when they differ or are unknown. */
export function currencyOf(
  currencies: ReadonlyMap<string, FundCurrency>,
  fundIds?: string | Iterable<string>,
): FundCurrency {
  if (fundIds === undefined) return 'ILS';
  const ids = typeof fundIds === 'string' ? [fundIds] : [...fundIds];
  const distinct = new Set(ids.map((id) => currencies.get(id) ?? 'ILS'));
  return distinct.size === 1 ? [...distinct][0] : 'ILS';
}

export type Money = (v: number | null | undefined, fundIds?: string | Iterable<string>) => string;

/** A formatter that writes an amount in the currency of the fund(s) it belongs to. */
export function useMoney(): Money {
  const currencies = useContext(FundCurrencyContext);
  return (v, fundIds) => fmtMoney(v, currencyOf(currencies, fundIds));
}
